import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAPI } from '../../../../Context/MyContext';
import { MESSAGE_TYPES } from '../nodeConfig';


function getDefaultPayloadByType(type) {
  switch (type) {
    case 'text':
      return {
        text: {
          body: ''
        }
      };

    case 'template':
      return {
        template: {
          template_id: '',
          name: '',
          language: '',
          components: []
        }
      };

    case 'image':
    case 'video':
    case 'document':
    case 'audio':
    case 'sticker':
      return {
        [type]: {
          link: '',
          caption: '',
          media_id: ''
        }
      };

    case 'location':
      return {
        location: {
          name: '',
          latitude: '',
          longitude: ''
        }
      };

    case 'contact':
      return {
        contacts: []
      };

    default:
      return {};
  }
}

function extractVariablesFromText(text = '') {
  const matches = [...text.matchAll(/{{\s*(\d+)\s*}}/g)];
  return matches.map((match) => match[1]);
}

function buildTemplateComponents(template) {
  const structured = [];

  (template?.components || []).forEach((component) => {
    if (component.type === 'BODY') {
      const variables = extractVariablesFromText(component.text || '');
      structured.push({
        type: 'body',
        text: component.text || '',
        parameters: variables.map((v) => ({ type: 'text', index: v, value: '' }))
      });
      return;
    }

    if (component.type === 'FOOTER') {
      structured.push({
        type: 'footer',
        text: component.text || '',
        parameters: []
      });
      return;
    }

    if (component.type === 'HEADER' && component.format === 'TEXT') {
      const variables = extractVariablesFromText(component.text || '');
      structured.push({
        type: 'header',
        format: 'text',
        text: component.text || '',
        parameters: variables.map((v) => ({ type: 'text', index: v, value: '' }))
      });
      return;
    }

    if (component.type === 'HEADER' && component.format === 'IMAGE') {
      const exampleUrl = component.example?.header_handle?.[0] || '';
      structured.push({
        type: 'header',
        format: 'image',
        exampleUrl,
        parameters: [{ type: 'image', value: exampleUrl }]
      });
    }
  });

  return structured;
}

export default function SendMessageSettings({ cfg, data, update }) {
  const { getPhoneAll, getTemplateByWabaId, uploadMediaToWhatsApp } = useAPI();
  const [phones, setPhones] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const updatePayload = (nextPayload) => update({ payload: nextPayload });
  const messageType = cfg.messageType || 'text';
  const payload = cfg.payload && typeof cfg.payload === 'object' ? cfg.payload : {};

  const selectedPhoneId = useMemo(() => {
    const explicit = cfg.phoneNumberId || cfg.phone_number_id || '';
    if (explicit && !String(explicit).includes('{{')) return explicit;
    return data?.workflowPhoneId || '';
  }, [cfg.phoneNumberId, cfg.phone_number_id, data?.workflowPhoneId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getPhoneAll();
        if (res?.success) setPhones(res.data || []);
      } catch (err) {
        console.error('Failed to load phones:', err);
      }
    })();
  }, [getPhoneAll]);

  useEffect(() => {
    if (messageType !== 'template') return;
    if (!selectedPhoneId || !phones.length) {
      setTemplates([]);
      return;
    }

    const selectedPhone = phones.find((p) => p.phone_number_id === selectedPhoneId);
    if (!selectedPhone?.waba_id) {
      setTemplates([]);
      return;
    }

    (async () => {
      try {
        setLoadingTemplates(true);
        const res = await getTemplateByWabaId(selectedPhone.waba_id);
        setTemplates(res?.data || []);
      } catch (err) {
        console.error('Failed to load templates:', err);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    })();
  }, [messageType, selectedPhoneId, phones, getTemplateByWabaId]);

  const handleTemplateSelect = (templateId) => {
    const selected = templates.find((t) => t.id === templateId);
    if (!selected) return;

    const nextTemplate = {
      template_id: selected.id,
      name: selected.name,
      language: selected.language,
      phone_number_id: selectedPhoneId,
      components: buildTemplateComponents(selected)
    };

    update({
      templateId: selected.id,
      templateName: selected.name,
      templateLanguage: selected.language,
      payload: { ...payload, template: nextTemplate }
    });
  };

  const handleUploadMedia = async () => {
    if (!mediaFile) {
      toast.error('Please select a file first.');
      return;
    }

    if (!selectedPhoneId) {
      toast.error('Please select WhatsApp phone number first.');
      return;
    }

    const formData = new FormData();
    formData.append('file', mediaFile);
    formData.append('phone_number_id', selectedPhoneId);

    try {
      setUploading(true);
      const result = await uploadMediaToWhatsApp(formData);

      update({
        payload: {
          ...payload,
          [messageType]: {
            ...(payload[messageType] || {}),
            media_id: result?.media_id || '',
            filename: mediaFile.name,
            mimeType: mediaFile.type,
            caption: payload?.[messageType]?.caption || cfg.mediaCaption || ''
          }
        }
      });
      toast.success('Media uploaded successfully.');
      setMediaFile(null);
    } catch (err) {
      console.error('Media upload failed:', err);
      toast.error(err?.response?.data?.message || 'Media upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-2 grid gap-2">
      {/* <label className="grid gap-1 text-xs text-slate-700">
        Recipient Source
        <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.recipientSource || cfg.to || ''} onChange={(e) => update({ recipientSource: e.target.value, to: e.target.value })} />
      </label> */}

      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
        {/* <label className="grid gap-1 text-xs text-slate-700">
          WhatsApp Business
          <select
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            value={selectedPhoneId}
            onChange={(e) => update({ phoneNumberId: e.target.value, phone_number_id: e.target.value })}
          >
            <option value="">Select WhatsApp Business</option>
            {phones.map((p) => (
              <option key={p.phone_number_id} value={p.phone_number_id}>
                {p.verified_name} ({p.waba_id})
              </option>
            ))}
          </select>
        </label> */}


        {/* <label className="grid gap-1 text-xs text-slate-700">
          Platform
          <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.platform || 'whatsapp'} onChange={(e) => update({ platform: e.target.value })} />
        </label> */}

        {/* <label className="grid gap-1 text-xs text-slate-700">
          Phone Number Id
          <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.phoneNumberId || '{{phone_number_id}}'} onChange={(e) => update({ phoneNumberId: e.target.value })} />
        </label> */}



      </div>

      <label className="grid gap-1 text-xs text-slate-700">
        Message Type
        <select className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          value={messageType}
          // onChange={(e) => update({ messageType: e.target.value })}
          onChange={(e) => {
            const newType = e.target.value;

            const newPayload = getDefaultPayloadByType(newType);

            update({
              messageType: newType,
              payload: newPayload,
              text: '',
              message: '',
              mediaUrl: '',
              mediaCaption: '',
              templateId: '',
              templateName: '',
              templateLanguage: ''
            });
          }}

        >
          {MESSAGE_TYPES.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </label>

      {messageType === 'text' ? (
        <label className="grid gap-1 text-xs text-slate-700">
          Text
          <textarea
            className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
            rows={3}
            placeholder='Use plain text or include variables wrapped in double curly braces, like {{variableName}}.'
            value={cfg.text || payload?.text?.body || ''}
            onChange={(e) => {
              const value = e.target.value;
              update({ text: value, message: value });
              updatePayload({ ...payload, text: { ...(payload.text || {}), body: value } });
            }}

          />
        </label>
      ) : null}

      {messageType === "template" && (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">

          {/* TEMPLATE SELECT */}
          <label className="grid gap-1 text-xs text-slate-700 sm:col-span-2">
            Select Template
            <select
              className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs"
              value={cfg.templateId || ""}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              disabled={!selectedPhoneId || loadingTemplates}
            >
              <option value="">
                {loadingTemplates ? "Loading templates..." : "Select Template"}
              </option>

              {templates
                .filter((t) => t.status === "APPROVED")
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.language})
                  </option>
                ))}
            </select>
          </label>

          {/* TEMPLATE NAME (READONLY) */}
          <label className="grid gap-1 text-xs text-slate-700">
            Template Name
            <input
              readOnly
              value={payload?.template?.name || ""}
              className="nodrag w-full rounded-lg border border-slate-200 bg-slate-100 text-slate-500 px-2 py-1.5 text-xs cursor-not-allowed"
            />
          </label>

          {/* LANGUAGE (READONLY) */}
          <label className="grid gap-1 text-xs text-slate-700">
            Language
            <input
              readOnly
              value={payload?.template?.language || ""}
              className="nodrag w-full rounded-lg border border-slate-200 bg-slate-100 text-slate-500 px-2 py-1.5 text-xs cursor-not-allowed"
            />
          </label>

          {/* TEMPLATE ID (READONLY) */}
          <label className="grid gap-1 text-xs text-slate-700 sm:col-span-2">
            Template ID
            <input
              readOnly
              value={payload?.template?.template_id || ""}
              className="nodrag w-full rounded-lg border border-slate-200 bg-slate-100 text-slate-500 px-2 py-1.5 text-xs cursor-not-allowed"
            />
          </label>

          {/* TEMPLATE COMPONENTS */}
          {payload?.template?.components?.map((component, compIndex) => {
            const { type, format, parameters, text } = component;

            return (
              <div
                key={compIndex}
                className="sm:col-span-2 border rounded-lg p-3 bg-white grid gap-2"
              >
                <div className="text-[11px] font-semibold text-slate-600">
                  {type.toUpperCase()}
                </div>

                {/* HEADER MEDIA PREVIEW */}
                {type === "header" && format === "image" && parameters?.[0]?.value && (
                  <div className="flex flex-col gap-2">
                    <img
                      src={parameters[0].value}
                      alt="header"
                      className="rounded-md border max-h-32 object-cover"
                    />

                    <input
                      readOnly
                      value={parameters[0].value}
                      className="nodrag w-full rounded-lg border border-slate-200 bg-slate-100 text-slate-500 px-2 py-1.5 text-xs cursor-not-allowed"
                    />

                    <span className="text-[10px] text-slate-400">
                      Locked – defined in approved template
                    </span>
                  </div>
                )}

                {/* BODY TEXT PREVIEW */}
                {type === "body" && text && (
                  <div className="text-xs text-slate-600 whitespace-pre-line bg-slate-50 p-2 rounded">
                    {text}
                  </div>
                )}

                {/* FOOTER */}
                {type === "footer" && (
                  <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                    {text}
                  </div>
                )}

                {/* VARIABLES */}
                {parameters?.map((param, paramIndex) => {
                  if (param.type === "image") return null;

                  return (
                    <label
                      key={paramIndex}
                      className="grid gap-1 text-xs text-slate-700"
                    >
                      Variable {`{{${param.index}}}`}

                      <input
                        className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs"
                        placeholder={`Value for {{${param.index}}}`}
                        value={param.value || ""}
                        onChange={(e) => {
                          const value = e.target.value;

                          const updatedComponents = [
                            ...payload.template.components
                          ];

                          updatedComponents[compIndex].parameters[paramIndex].value =
                            value;

                          updatePayload({
                            ...payload,
                            template: {
                              ...payload.template,
                              components: updatedComponents
                            }
                          });
                        }}
                      />
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {['image', 'video', 'document', 'audio', 'sticker'].includes(messageType) ? (
        <>
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-[1fr_auto] items-end">
            <label className="grid gap-1 text-xs text-slate-700">
              Upload Media (WhatsApp)
              <input
                type="file"
                className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900"
                accept={messageType === 'image' ? 'image/*' : messageType === 'video' ? 'video/*' : messageType === 'audio' ? 'audio/*' : '*/*'}
                onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
              />
            </label>
            <button
              type="button"
              className="nodrag rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-blue-700 disabled:opacity-60"
              onClick={handleUploadMedia}
              disabled={uploading || !mediaFile}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>

          {/* <label className="grid gap-1 text-xs text-slate-700">
            Media URL
            <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.mediaUrl || payload?.[messageType]?.link || ''} onChange={(e) => update({ mediaUrl: e.target.value, payload: { ...payload, [messageType]: { ...(payload[messageType] || {}), link: e.target.value } } })} />
          </label> */}

          <label className="grid gap-1 text-xs text-slate-700">
            Media Caption
            <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.mediaCaption || payload?.[messageType]?.caption || ''} onChange={(e) => update({ mediaCaption: e.target.value, payload: { ...payload, [messageType]: { ...(payload[messageType] || {}), caption: e.target.value } } })} />
          </label>
          {payload?.[messageType]?.media_id ? (
            <p className="text-[11px] text-green-700">Uploaded media id: {payload[messageType].media_id}</p>
          ) : null}
        </>
      ) : null}

      {messageType === 'location' ? (
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-3">
          <label className="grid gap-1 text-xs text-slate-700">
            Name
            <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.locationName} onChange={(e) => update({ locationName: e.target.value })} />
          </label>
          <label className="grid gap-1 text-xs text-slate-700">
            Latitude
            <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.latitude} onChange={(e) => update({ latitude: e.target.value })} />
          </label>
          <label className="grid gap-1 text-xs text-slate-700">
            Longitude
            <input className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" value={cfg.longitude} onChange={(e) => update({ longitude: e.target.value })} />
          </label>
        </div>
      ) : null}

      {messageType === 'contact' ? (
        <label className="grid gap-1 text-xs text-slate-700">
          Contact JSON
          <textarea className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" rows={3} value={cfg.contactJson} onChange={(e) => update({ contactJson: e.target.value })} />
        </label>
      ) : null}

      {messageType.startsWith('interactive') ? (
        <label className="grid gap-1 text-xs text-slate-700">
          Interactive JSON
          <textarea className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" rows={3} value={cfg.interactiveJson} onChange={(e) => update({ interactiveJson: e.target.value })} />
        </label>
      ) : null}

      {/* <label className="grid gap-1 text-xs text-slate-700">
        Template Components JSON (Legacy)
        <textarea
          className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-mono text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
          rows={3}
          value={JSON.stringify(payload?.template?.components || [], null, 2)}
          onChange={(e) => {
            try {
              const components = JSON.parse(e.target.value);
              updatePayload({ ...payload, template: { ...(payload.template || {}), components } });
            } catch { }
          }}
        />
      </label> */}

      {/* <label className="grid gap-1 text-xs text-slate-700">
        Variable Mapping JSON
        <textarea className="nodrag w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20" rows={3} value={cfg.variableMappingJson} onChange={(e) => update({ variableMappingJson: e.target.value })} />
      </label> */}
    </div>
  );
}

